import React, { useContext, useState } from 'react';

import cn from 'classnames';
import { format } from 'date-fns';

import CommentActionUI from './CommentActions';
import FlagButton from './FlagButton';
import PostForm from './PostForm';
import ReplyForm from './ReplyForm';
import ReplyUI from './ReplyUI';
import { REPLY_ITEMS_PER_PAGE } from '../../../apis/khoros/constants';
import { useKhorosReplies, useKhorosUser } from '../../../apis/khoros/hooks';
import { KhorosReplyItemSchema } from '../../../apis/khoros/type/khoros-replies.type';
import { useUserProfile } from '../../../apis/useUserSearch';
import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import I18nContext from '../../../aurr/contexts/I18nContext';
import LinkAsButton from '../../../helpers/LinkAsButton';
import { ProfileAvatar } from '../../container/user';
import { ProfileName } from '../../container/user/User';
import Skeleton from '../skeleton';

interface CommentProps {
  userContentURL?: string;
  userProfileURL?: string;
  baseURL?: string;
  topicId: string;
  comment: KhorosReplyItemSchema;
}

export default function PostUI({
  userProfileURL,
  userContentURL,
  baseURL,
  topicId,
  comment,
}: CommentProps) {
  const {
    kh_author_sso_id,
    kh_body,
    kh_post_time,
    kh_author_user_id,
    kh_post_id,
    kh_total_replies_count,
    kh_author_avatar,
    isOptimistic,
  } = comment;

  const { t } = useContext(I18nContext);
  const { USER_PROFILE_URL, USER_CONTENT_URL, KHOROS_API } =
    useContext(ConfigContext);
  const [isEditing, setIsEditing] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [isHover, setIsHover] = useState(false);

  const postInProgress = isOptimistic || kh_post_id === 'temporary-value';

  const {
    data: replies,
    isLoading: repliesIsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useKhorosReplies({
    topicId,
    baseURL: baseURL || KHOROS_API,
    commentId: kh_post_id,
    pageSize: REPLY_ITEMS_PER_PAGE,
    enabled: !postInProgress,
  });

  const { isLoading: profileIsLoading, data: profile } = useUserProfile({
    oxygenId: kh_author_sso_id,
    userProfileURL: userProfileURL || USER_PROFILE_URL,
    userContentURL: userContentURL || USER_CONTENT_URL,
  });

  const { data: khorosMyUser } = useKhorosUser({
    baseURL: baseURL || KHOROS_API,
  });

  const { kh_user_id: myUserId } = khorosMyUser || {};

  const { encryptedId, firstName, lastName, userName } = profile;

  const parsedName =
    firstName && lastName ? `${firstName} ${lastName}` : userName;

  const profileURL =
    !profileIsLoading && encryptedId
      ? `/autodesk-university/profile/${encodeURIComponent(encryptedId)}`
      : '';

  const parsedBody = decodeURI(
    kh_body.replace(/(\\r)|(\\n)/g, '\n').replace(/(<([^>]+)>)/gi, '')
  );

  const isAuthor =
    !!(kh_author_user_id && myUserId) && kh_author_user_id === myUserId;

  const closeEditing = () => {
    setIsEditing(false);
    setShowEditForm(false);
    setIsReplying(false);
  };

  const handleMouseIn = () => {
    setIsHover(true);
  };

  const handleMouseOut = () => {
    setIsHover(false);
  };

  return (
    <div className="au-flex au-flex-col au-gap-c24 !au-text-white">
      {showEditForm && (
        <PostForm
          comment={comment}
          baseURL={baseURL}
          topicId={topicId}
          userProfileURL={userProfileURL}
          userContentURL={userContentURL}
          onEditTentative={() => {
            setIsEditing(false);
          }}
          onEditSuccess={closeEditing}
          onEditFail={() => {
            setIsEditing(true);
          }}
        />
      )}
      {!isEditing && (
        <div
          className=""
          onMouseEnter={handleMouseIn}
          onMouseLeave={handleMouseOut}
        >
          <div
            className={cn('au-flex au-gap-2 au-items-start', {
              'au-opacity-30': postInProgress,
            })}
          >
            <ProfileAvatar
              alt={t('avatarFor', { ns: 'common', fullName: parsedName })}
              size="medium"
              src={kh_author_avatar}
              fullName={parsedName}
              isLoading={!khorosMyUser && profileIsLoading}
              oxygenId={kh_author_sso_id}
            />
            <div className="au-text-c14 au-grow au-relative">
              <ProfileName
                isLoading={profileIsLoading}
                fullName={parsedName}
                hasLink
                profileURL={profileURL}
                className="au-text-link-text"
              />
              <div className="au-whitespace-pre-wrap au-break-words">
                {parsedBody}
              </div>
              <span className="au-text-c12 au-text-gray-300">
                {format(new Date(kh_post_time), 'PP')}
              </span>
              {myUserId && (
                <>
                  &nbsp;|&nbsp;
                  <LinkAsButton
                    disabled={postInProgress}
                    className="!au-text-white"
                    as="button"
                    onClick={() => {
                      setIsReplying(prevState => !prevState);
                    }}
                  >
                    {t('comments.reply', { ns: 'common' })}
                  </LinkAsButton>
                </>
              )}
              <FlagButton
                baseURL={baseURL}
                commentId={kh_post_id}
                body={kh_body}
              />
              {isHover && !postInProgress ? (
                <CommentActionUI
                  isAuthor={isAuthor}
                  onEdit={() => {
                    setIsEditing(true);
                    setShowEditForm(true);
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
      {isReplying ? (
        <ReplyForm
          topicId={topicId}
          parentId={comment.kh_post_id}
          baseURL={baseURL}
          userContentURL={userContentURL}
          userProfileURL={userProfileURL}
        />
      ) : null}
      {kh_total_replies_count > 0 && repliesIsLoading && (
        <div className="au-flex au-space-x-2 au-pl-12">
          <Skeleton height={32} width={32} rounded />
          <div className="au-grow">
            <Skeleton height="10px" width="200px" />
            <Skeleton count={3} height="15px" width="100%" />
          </div>
        </div>
      )}
      {replies?.pages && replies.pages.length > 0 && (
        <>
          {!repliesIsLoading &&
            replies.pages.map((page, idx) => (
              <React.Fragment
                key={
                  page.kh_replies.length > 0
                    ? page.kh_replies[0].kh_post_id
                    : idx
                }
              >
                {page.kh_replies.map((reply: KhorosReplyItemSchema) => (
                  <ReplyUI
                    comment={reply}
                    key={reply.kh_post_id}
                    parentId={comment.kh_post_id}
                    baseURL={baseURL}
                    userContentURL={userContentURL}
                    userProfileURL={userProfileURL}
                    topicId={topicId}
                  />
                ))}
              </React.Fragment>
            ))}
          {isFetchingNextPage && (
            <div className="au-flex au-space-x-2 au-pl-12">
              <Skeleton height={32} width={32} rounded />
              <div className="au-grow">
                <Skeleton height="10px" width="200px" />
                <Skeleton count={3} height="15px" width="100%" />
              </div>
            </div>
          )}
          {hasNextPage && (
            <div className="au-pl-c88 au-mt-n-16">
              <LinkAsButton
                component="button"
                onClick={() => {
                  fetchNextPage();
                }}
                disabled={isFetchingNextPage || !hasNextPage}
              >
                {t('comments.loadMoreReplies', { ns: 'common' })}
              </LinkAsButton>
            </div>
          )}
        </>
      )}
    </div>
  );
}
